/**
 * Catches a cart that Shopify's "Buy again" filled in, and offers the builder instead.
 *
 * The tell is a cart holding lunch lines with **no `Delivery Date` property on any of them**.
 * Nothing the funnel adds can look like that — it writes one line per delivery day, each with
 * its date — so the only way to reach this state is a re-add that dropped the properties, which
 * is exactly what "Buy again" does.
 *
 * What happens on accept:
 *   1. this device's memory is searched for the order those same variants came from, so the
 *      bread, the removals and the student's name come back too (see order-memory.js);
 *   2. only the property-less lines are removed — a legitimate funnel order sitting in the same
 *      cart is left completely alone;
 *   3. the funnel is seeded and opened, landing the customer on the calendar.
 *
 * With no matching memory the products still carry over, and the customer re-picks the bread and
 * the name. That is worse than a full repeat but far better than a cart Shopify cannot deliver.
 */
class CartRepeat extends HTMLElement {
  connectedCallback() {
    this.builderUrl = this.dataset.builderUrl || '/';
    this.action = this.querySelector('[data-repeat-action]');
    this.text = this.querySelector('[data-repeat-text]');

    this.action?.addEventListener('click', () => this.repeat());
    this.check();

    // The cart page re-renders its sections on every quantity change; re-check when it does.
    document.addEventListener('cart:refresh', () => this.check());
  }

  static get DATE_PROPERTY() {
    return 'Delivery Date';
  }

  /** Lines that are missing a delivery day, paired with their 1-based cart index. */
  static orphansIn(cart) {
    return (cart.items || [])
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => !(item.properties || {})[CartRepeat.DATE_PROPERTY]);
  }

  async check() {
    try {
      const cart = await fetch(`${window.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      }).then((response) => response.json());

      this.orphans = CartRepeat.orphansIn(cart);
      const show = this.orphans.length > 0;
      this.toggleAttribute('hidden', !show);
      if (!show) return;

      const variants = this.orphans.map(({ item }) => String(item.variant_id));
      this.remembered = window.OrderMemory?.matching(variants) || null;

      if (this.remembered && this.text) {
        this.text.textContent =
          'We still have the rest of this order on this device — the bread, anything you took off, ' +
          'and who it was for. Take it into the builder and all you pick is the delivery days.';
      }
    } catch (error) {
      // A cart we cannot read is a cart we must not second-guess; leave the page as it was.
      this.setAttribute('hidden', '');
    }
  }

  /**
   * Seeds the funnel from the remembered order, or — failing that — from whatever the cart can
   * still tell us, which is the products and nothing else.
   */
  seedFrom(orphans) {
    if (this.remembered) return this.remembered;

    const lunch = orphans.map(({ item }) => item);
    if (!lunch.length) return null;

    /*
     * Which line is the sandwich cannot be read off the cart — a cart line knows its variant and
     * its price, not what step of the funnel it came from. The most expensive line is the
     * sandwich in every combination the shop sells (the dearest add-on is $3.85 against the
     * cheapest sandwich at $8.05), and if that ever stops being true the funnel simply opens on
     * a different sandwich, which the customer sees and can change.
     */
    const sandwich = lunch.reduce((dearest, item) => (item.price > dearest.price ? item : dearest), lunch[0]);

    return {
      id: `cart${Date.now()}`,
      savedAt: new Date().toISOString(),
      student: '',
      quantity: 1,
      days: 0,
      product: { variantId: String(sandwich.variant_id), label: sandwich.product_title },
      build: null,
      addons: lunch
        .filter((item) => item !== sandwich)
        .map((item) => ({ key: '', variantId: String(item.variant_id), label: item.product_title })),
    };
  }

  async repeat() {
    const orphans = this.orphans || [];
    const seed = this.seedFrom(orphans);
    if (!seed) return;

    this.action.setAttribute('aria-disabled', 'true');
    this.action.disabled = true;

    try {
      /*
       * Removed highest line first: every removal renumbers the lines beneath it, so walking
       * down the list would delete the wrong rows once the first one went.
       */
      const lines = orphans.map(({ line }) => line).sort((a, b) => b - a);
      for (const line of lines) {
        await fetch(`${window.routes.cart_change_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity: 0 }),
        });
      }
    } catch (error) {
      /*
       * The lines could not be cleared. Carrying on regardless would hand the customer a builder
       * whose order lands on top of the ones already in the cart, so stop and let them see the
       * cart they still have.
       */
      console.error(error);
      this.action.removeAttribute('aria-disabled');
      this.action.disabled = false;
      return;
    }

    window.OrderMemory?.putSeed(seed);
    window.location = this.builderUrl;
  }
}

if (!customElements.get('cart-repeat')) {
  customElements.define('cart-repeat', CartRepeat);
}
